import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { faLink } from '@fortawesome/pro-light-svg-icons/faLink';
import { BioLinkAutoAddResult } from 'fym-shared/src/smartlinks/bioLinkSettings';
import { TypedMemo } from '../../../../common/types';
import { SMLButton } from '../../../../common/views/SMLButton/SMLButton';
import { addSongFlowMessages } from '../addSongFlowMessages';
import { useDoneBioLinkRow } from './useDoneBioLinkRow';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole page; each part uses its own slice of it */
import styles from '../CatalogAddSong.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface DoneBioLinkRowProps {
  accountId: number;
  itemUuid: string;
  title: string;
  /** True when the song is out: the block shows the play buttons, not the pre-save. */
  isOut: boolean;
  result: BioLinkAutoAddResult | undefined;
}

/**
 * The row above the next actions of Done (design smartlink-editor e2): the song is on the Bio Link
 * (Open, Undo), waits for a publish, or is not there (Add).
 */
const DoneBioLinkRowComponent: React.FC<DoneBioLinkRowProps> = ({ accountId, itemUuid, title, isOut, result }) => {
  const intl = useIntl();
  const row = useDoneBioLinkRow(accountId, itemUuid, result);
  if (row === undefined) {
    return null;
  }
  const { state } = row;
  let heading: string;
  let body: string | undefined;
  if (state.kind === 'live') {
    heading = intl.formatMessage(addSongFlowMessages.doneBioLive);
    body = intl.formatMessage(addSongFlowMessages.doneBioLiveBody, {
      address: row.address,
      title,
      kind: isOut ? 'play' : 'presave',
      place: row.place,
    });
  } else if (state.kind === 'notLive') {
    heading = intl.formatMessage(addSongFlowMessages.doneBioNotLive);
  } else if (state.kind === 'draftRemoved') {
    heading = intl.formatMessage(addSongFlowMessages.doneBioDraftRemoved);
  } else if (row.isUndone) {
    heading = intl.formatMessage(addSongFlowMessages.doneBioUndone);
  } else {
    heading = intl.formatMessage(addSongFlowMessages.doneBioAdd, { title });
    body = intl.formatMessage(addSongFlowMessages.doneBioAddBody);
  }
  const isOn = state.kind === 'live' || state.kind === 'notLive';

  return (
    <div className={clsx(styles.onBio, state.kind === 'live' && styles.onBioLive)} data-testid="add-song-done-bio">
      <span className={clsx(styles.kbox, isOn && styles.kboxOk)}>
        <FontAwesomeIcon icon={isOn ? faCheck : faLink} />
      </span>
      <div className={styles.grow}>
        <b>{heading}</b>
        {body !== undefined && <small>{body}</small>}
        {row.hasError && (
          <small className={styles.error} role="alert">
            {intl.formatMessage(addSongFlowMessages.doneBioFailed)}
          </small>
        )}
      </div>
      {state.kind === 'absent' ? (
        <SMLButton size="sm" omitCap isLoading={row.isBusy} onClick={row.add} testID="add-song-done-bio-add">
          {intl.formatMessage(
            row.isUndone ? addSongFlowMessages.doneBioAddItButton : addSongFlowMessages.doneBioAddButton
          )}
        </SMLButton>
      ) : (
        <SMLButton size="sm" outline omitCap onClick={row.open} testID="add-song-done-bio-open">
          {intl.formatMessage(addSongFlowMessages.doneBioOpen)}
        </SMLButton>
      )}
      {state.kind === 'live' && (
        <SMLButton
          size="sm"
          color="link"
          omitCap
          isLoading={row.isBusy}
          onClick={row.undo}
          testID="add-song-done-bio-undo"
        >
          {intl.formatMessage(addSongFlowMessages.doneBioUndo)}
        </SMLButton>
      )}
    </div>
  );
};

export const DoneBioLinkRow = TypedMemo(DoneBioLinkRowComponent);
